import * as Sentry from '@sentry/vue';
import _ from 'lodash';
import jwt from 'jsonwebtoken';
import Vue from 'vue';
import Vuex from 'vuex';
import liff from '@line/liff';
import Sdk from '@/sdk';
import comics from './comics';
import config from './config';

Vue.use(Vuex);

export default new Vuex.Store({
    modules: {
        comics,
        config,
    },
    state: {
        user: undefined,
        defaultName: undefined,
        isContactDataExist: false,
        score: undefined,
        liffInitFail: false,

        // game
        questionSet: [],
        answerCorrectedSet: [],
        correctSet: [],
        incorrectSet: [],
        newCorrect: undefined,
        highestScore: undefined,

        // Achievement
        towerUnlocked: {},
        accumulatedScore: undefined,
        trophyFlag: undefined,
        loginDayCount: 0,
        // Daily

        // Review
        reviewQuestionSet: [],
    },
    getters: {
        isInEvent(state) {
            return !!state.user?.event;
        },
        joinedEvent(state) {
            return _.get(state.user, 'event.id');
        },
    },
    mutations: {
        setDefaultName(state, _name) {
            state.defaultName = _name;
        },
        setUser(state, _user) {
            state.user = _user;
        },
        setContactFormState(state, _state) {
            state.isContactDataExist = _state;
        },
        setScore(state, _score) {
            state.score = _score;
        },
        updateToken(state, token) {
            if (token) {
                const profile = jwt.decode(token);
                state.user = profile.data;
                console.log('user data update', state.user);
            } else {
                state.user = null;
                state.accumulatedScore = undefined;
                state.trophyFlag = undefined;
                state.loginDayCount = 0;
            }
        },
        raiseLiffInitFail(state) {
            state.liffInitFail = true;
        },

        // game
        initGameData(state) {
            state.score = undefined;
            state.correctSet = [];
            state.questionSet = [];
            state.incorrectSet = [];
            state.newCorrect = undefined;
            state.trophyFlag = undefined;
            state.reviewQuestionSet = [];
        },
        initQuestionSet(state, _set) {
            state.questionSet = _set;
        },
        initAnswerCorrectedSet(state, _set) {
            state.answerCorrectedSet = _set;
        },
        initHighestScore(state, _score) {
            state.highestScore = _score;
        },

        updateCorrectSet(state, _id) {
            state.correctSet.push(_id);
        },
        updateIncorrectSet(state, _id) {
            state.incorrectSet.push(_id);
        },
        updateNewCorrect(state, _newCorrect) {
            state.newCorrect = _newCorrect;
        },
        // Achievement
        updateTowerUnlocked(state, _progress) {
            state.towerUnlocked = _progress;
        },
        updateAccumulatedScore(state, _score) {
            state.accumulatedScore = _score;
        },
        updateLoginDayCount(state, _count) {
            state.loginDayCount = _count;
        },

        // Review
        updateReviewQuestionSet(state, _list) {
            state.reviewQuestionSet = _.reverse(_list);
        },
    },
    actions: {
        login({ state }) {
            if (state.liffInitFail) {
                alert('無法初始化line服務，請使用訪客體驗遊玩遊戲');
            } else liff.login();
        },
        async emptyNameHotUpdate({ state }) {
            if (state.defaultName) {
                Sdk.updateUserProfile({ name: state.defaultName });
            }
        },
        // game
        async fetchGameInformation({ commit, getters }, { GAME, LEVEL }) {
            let RES = null;
            try {
                const event = getters.joinedEvent;
                if (event) RES = await Sdk.getEventQuestions(event, GAME, LEVEL);
                else RES = await Sdk.getQuestions(GAME, LEVEL);
                commit('initQuestionSet', RES.all);
                commit('initAnswerCorrectedSet', RES.answerCorrected);
                commit('initHighestScore', RES.highestScore);
            } catch (e) {
                if (e?.response?.status === 401) {
                    // 由 SDK 處理錯誤
                    return;
                }
                console.error(e);
                throw Error('遊戲初始化有誤，請重新整理後再試');
            }
            if (RES.isPlayed) throw Error('活動每個關卡只能玩一次');
        },
        async handleGameOver({ commit, state, getters }, { game, level, answerTime }) {
            let { highestScore } = state;
            if (!state.highestScore) highestScore = 0;
            if (state.score > state.highestScore) highestScore = state.score;
            if (state.answerCorrectedSet) {
                let newCorrect = 0;
                state.correctSet.forEach((item) => {
                    const FOUND = _.find(state.answerCorrectedSet, (o) => o === item);
                    if (!FOUND) newCorrect += 1;
                });
                commit('updateNewCorrect', newCorrect);
            } else commit('updateNewCorrect', state.correctSet.length);

            const GAME = `game${game}`;
            const UNLOCK_THRESHOLD = getters['config/getTrophyThreshold'](GAME, level);
            if (!state.user) return;

            try {
                const basicParams = {
                    game,
                    level,
                    right: state.correctSet,
                    wrong: state.incorrectSet,
                };
                if (getters.isInEvent) {
                    await Sdk.submitEventResult({
                        ...basicParams,
                        event: getters.joinedEvent,
                        score: state.score,
                        answerTime,
                    });
                } else {
                    let trophy = state.towerUnlocked[`game${game}`][`level${level}`];
                    if (Math.floor(state.score / UNLOCK_THRESHOLD) - 1 > trophy) {
                        trophy += 1;
                        state.trophyFlag = trophy;
                    }
                    await Sdk.submitResult({
                        ...basicParams,
                        highestScore,
                        score: state.score,
                        trophy,
                    });
                }
            } catch (e) {
                Sentry.captureException(`handleGameOver(): game ${game}, level ${level}, event ${getters.joinedEvent}`);
                alert('本次遊戲暫時無法傳送紀錄，請重新整理再次遊玩');
            }
        },

        // tower
        async fetchTower({ state, commit }) {
            if (!state.user) return;
            const RES = await Sdk.getRecords();
            const UNLOCK = {};
            const ACCUMULATED_SCORE = {};

            for (let game = 1; game <= 2; game += 1) {
                const GAME_TROPHY = {};
                const GAME_ACCUMULATED_SCORE = {};
                for (let level = 1; level <= 3; level += 1) {
                    const FOUND = _.find(RES, (o) => o.game === game && o.level === level);
                    if (FOUND && FOUND.trophy) GAME_TROPHY[`level${level}`] = FOUND.trophy;
                    else GAME_TROPHY[`level${level}`] = 0;
                    if (FOUND && FOUND.accumulatedScore) {
                        GAME_ACCUMULATED_SCORE[`level${level}`] = FOUND.accumulatedScore;
                    } else GAME_ACCUMULATED_SCORE[`level${level}`] = 0;
                }
                UNLOCK[`game${game}`] = GAME_TROPHY;
                ACCUMULATED_SCORE[`game${game}`] = GAME_ACCUMULATED_SCORE;
            }
            commit('updateTowerUnlocked', UNLOCK);
            commit('updateAccumulatedScore', ACCUMULATED_SCORE);
        },
    },
});
